// vue-i18n plural rules for locales whose counts English's one/other can't spell.
//
// A plural message on the client is pipe-separated ("1 hike | {count} hikes")
// and a count passed to `t` picks the form. vue-i18n's built-in rule only knows the
// English shape, so a five-form Irish message ("1 siúlóir | {count} shiúlóir |
// …") would read its first two forms and never the lenited/eclipsed ones CLDR
// gives 2, 3–6 and 7–10. This is the client half of config/locales/plurals.rb,
// which teaches Rails the same rule.
//
// Pure and dependency-free so it can be unit-tested without a DOM.

/** vue-i18n's PluralizationRule: which of `choicesLength` forms a count reads. */
export type PluralRule = (choice: number, choicesLength: number, orgRule?: PluralRule) => number;

export type IrishPluralCategory = 'one' | 'two' | 'few' | 'many' | 'other';

/** The order an Irish five-form message lists its forms in. */
export const IRISH_PLURAL_FORMS: readonly IrishPluralCategory[] = ['one', 'two', 'few', 'many', 'other'];

/** CLDR's Irish rule, as config/locales/plurals.rb spells it. */
export function irishPluralCategory(count: number): IrishPluralCategory {
  const n = Math.abs(count);
  if (!Number.isInteger(n)) return 'other';
  if (n === 1) return 'one';
  if (n === 2) return 'two';
  if (n >= 3 && n <= 6) return 'few';
  if (n >= 7 && n <= 10) return 'many';
  return 'other';
}

/**
 * vue-i18n's own default (core-base's pluralDefault), for a message that doesn't
 * carry Irish's five forms. vue-i18n hands it in as `orgRule`; this copy only
 * runs when something calls the rule directly.
 */
function englishShapedIndex(choice: number, choicesLength: number): number {
  const n = Math.abs(choice);
  if (choicesLength === 2) return n ? (n > 1 ? 1 : 0) : 1;
  return n ? Math.min(n, 2) : 0;
}

/**
 * Five forms: CLDR's one/two/few/many/other. Anything else is an older message
 * written one/other (most of the Irish file still is), which keeps vue-i18n's
 * default so it reads exactly as it did before this rule existed.
 */
export const irishPluralRule: PluralRule = (choice, choicesLength, orgRule) => {
  if (choicesLength === IRISH_PLURAL_FORMS.length) {
    return IRISH_PLURAL_FORMS.indexOf(irishPluralCategory(choice));
  }
  return orgRule ? orgRule(choice, choicesLength) : englishShapedIndex(choice, choicesLength);
};

/** createI18n's `pluralRules`: only the locales that need more than one/other. */
export const pluralRules: Record<string, PluralRule> = { ga: irishPluralRule };
